import type { SchoolStaffRow } from '../../../shared/api/staff'
import type { ClassSummary, SubjectRow } from '../../../shared/api/classes'

/**
 * E3/E4's decisions, out of the component.
 *
 * 🔴 THE REPO HAS NO DOM RENDERER (OD-60), so a branch made inside a `.tsx` is a
 * branch no test can reach — and one of the branches here decides whether an
 * admin is shown a button that revokes a colleague's login. `platformModel.ts`
 * and `schoolDashboardModel.ts` are the precedent for every screen in this tier.
 */

/**
 * What an admin actually needs to know about a staff row, as one word.
 *
 * 🔴 `isActive` ALONE CANNOT SAY IT, AND THAT IS THE WHOLE REASON THIS EXISTS.
 * An account waiting on an invitation and an account that has been deactivated
 * are both `isActive: false`. They need opposite actions — re-send the link
 * versus leave them alone — and an admin shown "cannot sign in" beside a teacher
 * they invited ten minutes ago will create the account a second time.
 *
 * The fourth state is the one a naive read misses: an invitation that ran out.
 * The server reports `invitePending: false` for it, so it lands in `INVITED_EXPIRED`
 * rather than in `DEACTIVATED` — different sentence, same action as never having
 * been invited.
 */
export type StaffState = 'ACTIVE' | 'INVITE_PENDING' | 'INVITE_EXPIRED' | 'DEACTIVATED'

export function staffState(row: SchoolStaffRow): StaffState {
  if (row.isActive) return 'ACTIVE'
  if (row.invitePending) return 'INVITE_PENDING'
  /*
   * ⚠️ AN INVITE ROW THAT IS NO LONGER PENDING MEANS IT EXPIRED — a claimed one
   * would have made the account active, and a revoked one is excluded by the
   * server's query. Never invited at all also lands here, and the action is the
   * same: send one.
   */
  if (row.inviteExpiresAt !== null) return 'INVITE_EXPIRED'
  return 'DEACTIVATED'
}

/**
 * May this row be deactivated?
 *
 * 🔴 D4 — A CONTROL THAT CANNOT WORK IS WORSE THAN NO CONTROL. The server only
 * deactivates a `TEACHER` in the admin's own school (`ClassesService.deactivateTeacher`
 * refuses anything else with a 403), and deactivating somebody who already
 * cannot sign in does nothing. Offering the button in either case teaches an
 * admin that the screen is unreliable.
 */
export function mayDeactivate(row: SchoolStaffRow): boolean {
  return row.role === 'TEACHER' && row.isActive
}

/**
 * May an admin set this row's password?
 *
 * ✅ THIS WAS `mayInvite`, AND ITS OLD COMMENT IS THE INTERESTING PART: *"an
 * active account may not be invited — that would be a password reset by another
 * name, which this product deliberately does not have"*. It has one now, because
 * a school that issues its own credentials must be able to replace one; without
 * it, a teacher who forgets their password has no route back at all.
 *
 * 🔴 SO IT IS OFFERED FOR AN ACTIVE ACCOUNT TOO. The reset is the point, not a
 * side effect — and the row that most needs it is the one still waiting on an
 * invitation nobody will ever send.
 *
 * ⚠️ TEACHERS ONLY, MIRRORING `StaffService.setTeacherPassword`. A principal or
 * a coordinator in this list is somebody the route refuses; D4 — a control that
 * cannot work teaches an admin that the screen is unreliable. Admins are not in
 * this list at all (managing them is the operator tier).
 */
export function maySetPassword(row: SchoolStaffRow): boolean {
  return row.role === 'TEACHER'
}

/**
 * The teachers an admin may put in front of a class.
 *
 * 🔴 IT MIRRORS `resolveTeacher` ON THE SERVER, AND IT IS NOT THE CONTROL. Four
 * conditions there: exists · same school · is a TEACHER · is active. The list is
 * already same-school by construction, so this applies the other two — and the
 * server still refuses anything else, generically, because a client-side filter
 * on a list a client was given is not a security boundary. What it buys is that
 * an admin is never offered a name that would come back refused.
 */
export function assignableTeachers(rows: readonly SchoolStaffRow[]): SchoolStaffRow[] {
  return rows.filter((r) => r.role === 'TEACHER' && r.isActive)
}

/**
 * The classes an admin may assign, in the order they should be read.
 *
 * 🔴 UNASSIGNED FIRST. E4 exists to fix exactly one thing — a class nobody
 * teaches — and a class with no teacher is invisible to every teacher-scoped
 * read in the product: `ScopeService` scopes a teacher to `class.teacherId`, so
 * an unassigned class has no teacher who can open its roster, launch a session
 * or see its reports. Sorting it to the top makes the screen's purpose its first
 * row rather than something to hunt for.
 *
 * ⚠️ ARCHIVED CLASSES ARE EXCLUDED. Assigning a teacher to last year's cohort is
 * a control that does nothing useful and a roster they could then open.
 */
export function assignableClasses(classes: readonly ClassSummary[]): ClassSummary[] {
  return [...classes]
    .filter((c) => c.archivedAt === null)
    .sort((a, b) => {
      const aUnassigned = a.teacher === null
      const bUnassigned = b.teacher === null
      if (aUnassigned !== bUnassigned) return aUnassigned ? -1 : 1
      return a.name.localeCompare(b.name, undefined, { numeric: true })
    })
}

/**
 * How many live classes have nobody teaching them.
 *
 * ⚠️ A COUNT WITH ITS DENOMINATOR AT THE CALL SITE, never a percentage — OD-39
 * and OD-72 apply to this tier as much as to the dashboards.
 */
export function unassignedClassCount(classes: readonly ClassSummary[]): number {
  return classes.filter((c) => c.archivedAt === null && c.teacher === null).length
}

/* ──────────────────────────────────────── E4 · subjects on E3 ── */

/** One subject a named teacher takes in one class. */
export interface SubjectTeaching {
  classId: string
  className: string
  subjectId: string
  subjectName: string
}

/** The same fact read from the class's end: who takes what in one class. */
export interface ClassSubjectTeacher extends SubjectTeaching {
  teacherId: string
  teacherName: string
}

/**
 * The subject names a teacher takes, each named once.
 *
 * 🔴 DEDUPED, BECAUSE THE SAME SUBJECT IN THREE CLASSES IS ONE ANSWER TO THE
 * QUESTION THIS COLUMN ASKS. A Maths teacher with 2A, 2B and 3A would otherwise
 * read "Maths, Maths, Maths" in a cell four words wide, and an admin scanning for
 * the person who takes Rhymes would have to read past it three times.
 *
 * ⚠️ CASE IS PRESERVED AND NOT COMPARED. The server refuses a second "maths"
 * beside "Maths" in the same school (`SubjectsService.assertNameFree`), so two
 * spellings here would mean two schools' rows in one list — which cannot happen.
 */
export function subjectNames(row: SchoolStaffRow): string[] {
  const seen = new Set<string>()
  const names: string[] = []
  /* ⚠️ `?? []` AGAINST A SERVER THAT HAS NOT SHIPPED THIS FIELD YET, and it is
     not defensive typing for its own sake: the type says the field is there
     because the server sends it, and a deployed API one release behind sends a
     row without it. `undefined.map` is a white screen on the whole Teachers
     page, in exchange for a column that would have read "None". */
  for (const s of row.subjects ?? []) {
    if (seen.has(s.subjectId)) continue
    seen.add(s.subjectId)
    names.push(s.subjectName)
  }
  return names.sort((a, b) => a.localeCompare(b))
}

/**
 * Who takes which subject in one class, read out of the staff list.
 *
 * 🔴 NO SECOND REQUEST, AND THE REASON IS THAT THE STAFF LIST ALREADY CARRIES
 * THE PAIR. `GET /school/staff` returns each teacher's subject rows with the
 * class on them, so a per-class question is a regroup rather than one
 * `GET /classes/:id/subjects` per row — which at twelve classes is twelve
 * requests to fill one column.
 *
 * ⚠️ A SUBJECT WITH NO TEACHER OF ITS OWN IS NOT HERE, AND THAT IS CORRECT ON
 * THIS SCREEN. `ClassSubject.teacherId` null means *"the class teacher takes
 * it"* — already answered by the teacher dropdown in the same row. E4's class
 * screen is where a curriculum is edited; this column answers only *"who else
 * stands in front of this class"*.
 */
export function subjectTeachersFor(
  classId: string,
  staff: readonly SchoolStaffRow[],
): ClassSubjectTeacher[] {
  const rows: ClassSubjectTeacher[] = []
  for (const person of staff) {
    // `?? []` for `subjectNames`' reason — an API one release behind.
    for (const s of person.subjects ?? []) {
      if (s.classId !== classId) continue
      rows.push({
        ...s,
        teacherId: person.userId,
        teacherName: person.name ?? person.email ?? person.userId,
      })
    }
  }
  return rows.sort((a, b) => a.subjectName.localeCompare(b.subjectName))
}

/**
 * The school's subject with this name, if it already has one.
 *
 * 🔴 CASE-INSENSITIVE, MIRRORING THE SERVER, AND THIS IS THE WHOLE POINT OF
 * MATCHING AT ALL. `SubjectsService.assertNameFree` compares insensitively and
 * answers 409, so an admin typing "maths" beside an existing "Maths" would
 * otherwise be told their teacher could not be added — for a reason that reads
 * like a bug. Matching first turns that into *"we already have this one"* and
 * uses it.
 *
 * ⚠️ TRIMMED, BECAUSE THE SERVER TRIMS. A trailing space would otherwise create
 * a second subject the server would then refuse.
 */
export function matchSubjectName(
  name: string,
  subjects: readonly SubjectRow[],
): SubjectRow | null {
  const wanted = name.trim().toLowerCase()
  if (wanted.length === 0) return null
  return subjects.find((s) => s.name.trim().toLowerCase() === wanted) ?? null
}

/**
 * The classes an admin may hand a new teacher, in the order they should be read.
 *
 * ⚠️ ARCHIVED CLASSES ARE EXCLUDED for `assignableClasses`' reason, and the
 * server refuses them anyway (`SubjectsService.assertClassLive`): last year's
 * cohort has left, and putting a new teacher in front of it is a control that
 * does nothing.
 */
export function subjectableClasses(classes: readonly ClassSummary[]): ClassSummary[] {
  return classes
    .filter((c) => c.archivedAt === null)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
}

/**
 * The claim URL an admin hands over.
 *
 * 🔴 BUILT FROM `window.location`, NOT FROM A CONFIGURED BASE. The invitation is
 * claimed in this same application at `#/invite/<token>`, and the origin a
 * teacher must use is the one the admin is already on — a school on a private
 * deployment would otherwise be handed a link to somebody else's hostname. Taken
 * as an argument so it can be tested without a browser.
 *
 * ⚠️ THE TOKEN IS IN THE HASH, WHICH IS WHERE IT BELONGS. A hash fragment is not
 * sent to the server in a request line, so it does not land in access logs or in
 * a `Referer` header on the way out — and `routes.ts` already treats everything
 * after `invite/` as a credential rather than a sub-page.
 */
export function inviteLink(origin: string, pathname: string, token: string): string {
  return `${origin}${pathname}#/invite/${token}`
}

export interface StaffFilters {
  query: string
  /** `''` is every state. */
  state: StaffState | ''
  /** `''` is every class. */
  classId: string
}

/** The Teachers toolbar, applied. */
export function filterStaff(rows: readonly SchoolStaffRow[], f: StaffFilters): SchoolStaffRow[] {
  const q = f.query.trim().toLowerCase()
  return rows.filter((r) => {
    if (f.state !== '' && staffState(r) !== f.state) return false
    if (f.classId !== '' && !r.classes.some((c) => c.classId === f.classId)) return false
    if (q === '') return true
    return (
      (r.name?.toLowerCase().includes(q) ?? false) ||
      (r.email?.toLowerCase().includes(q) ?? false) ||
      r.classes.some((c) => c.name.toLowerCase().includes(q))
    )
  })
}

/**
 * The filtered staff list as CSV.
 *
 * 🔴 THE FORMULA GUARD, FOR `classesToCsv`'s REASON — a name is typed by a person.
 */
export function staffToCsv(rows: readonly SchoolStaffRow[]): string {
  const esc = (v: string | number): string => {
    const s = String(v)
    const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s
    return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe
  }
  const lines = [['Name', 'Email', 'Role', 'Status', 'Classes', 'Subjects'].map(esc).join(',')]
  for (const r of rows) {
    lines.push(
      [
        r.name ?? '',
        r.email ?? '',
        r.role,
        staffState(r),
        r.classes.map((c) => c.name).join('; '),
        r.subjects.map((s) => `${s.subjectName} (${s.className})`).join('; '),
      ]
        .map(esc)
        .join(','),
    )
  }
  return lines.join('\r\n')
}
